import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { PublicEdufadSummary } from "@edufadder/shared-types";
import { colors, radii, spacing } from "@edufadder/ui";
import { topicLabel } from "../components/EvidenceChip.js";
import * as api from "../lib/api.js";
import * as s from "../styles.js";
import { EdufadCard } from "./HomePage.js";

const STRENGTH_ORDER = ["not_adequately_supported", "mixed_or_contested", "insufficient_evidence", "supported"];

export function EdufadsPage(): JSX.Element {
  const [items, setItems] = useState<PublicEdufadSummary[] | null>(null);
  const [query, setQuery] = useState("");
  const [topic, setTopic] = useState<string>("all");

  useEffect(() => {
    api.listPublicEdufads().then(setItems).catch(() => setItems([]));
  }, []);

  const topics = useMemo(() => Array.from(new Set((items ?? []).map((i) => i.topic))).sort(), [items]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? [])
      .filter((i) => topic === "all" || i.topic === topic)
      .filter((i) => !q || i.label.toLowerCase().includes(q) || i.summary.toLowerCase().includes(q) || topicLabel(i.topic).toLowerCase().includes(q))
      .sort((a, b) => STRENGTH_ORDER.indexOf(a.evidence_strength) - STRENGTH_ORDER.indexOf(b.evidence_strength) || a.label.localeCompare(b.label));
  }, [items, query, topic]);

  return (
    <div style={s.wide}>
      <h1 style={{ ...s.heading1, fontSize: 34, color: colors.brand }}>The edufads library</h1>
      <hr style={s.goldRule} />
      <p style={{ ...s.lead, maxWidth: 760 }}>
        Popular ideas about teaching and learning, with a curated summary of what research says about each — including
        ideas that are <strong>well supported</strong>, so you can see what tends to work as well as what doesn't. This
        is everything the extension can recognise; it grows as curators publish new entries, and the extension picks
        them up automatically.
      </p>

      <div style={{ display: "flex", gap: spacing(3), flexWrap: "wrap", alignItems: "center", margin: `${spacing(6)} 0 ${spacing(4)}` }}>
        <label htmlFor="ef-search" style={{ position: "absolute", left: -9999 }}>
          Search edufads
        </label>
        <input
          id="ef-search"
          type="search"
          placeholder="Search, e.g. “brain”, “study”, “music”"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ ...s.input, maxWidth: 360, marginBottom: 0, fontSize: 15 }}
        />
        <div role="group" aria-label="Filter by topic" style={{ display: "flex", gap: spacing(2), flexWrap: "wrap" }}>
          {["all", ...topics].map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={topic === t}
              onClick={() => setTopic(t)}
              style={{
                padding: `${spacing(1)} ${spacing(3)}`,
                borderRadius: radii.pill,
                border: `1px solid ${topic === t ? colors.brand : colors.border}`,
                background: topic === t ? colors.brand : "#fff",
                color: topic === t ? "#fff" : colors.ink,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {t === "all" ? "All topics" : topicLabel(t)}
            </button>
          ))}
        </div>
      </div>

      {items === null ? (
        <p style={s.mutedText}>Loading…</p>
      ) : filtered.length === 0 ? (
        <p>
          Nothing matches that yet. <Link to="/suggest">Suggest an edufad</Link> for curators to research.
        </p>
      ) : (
        <div className="ef-grid-3">
          {filtered.map((e) => (
            <EdufadCard key={e.id} id={e.id} label={e.label} topic={e.topic} strength={e.evidence_strength} extra={e.summary.length > 150 ? `${e.summary.slice(0, 147)}…` : e.summary} />
          ))}
        </div>
      )}

      <div style={{ ...s.card, marginTop: spacing(8), background: colors.goldTint }}>
        <strong>Seen an idea that should be here?</strong> <Link to="/suggest">Suggest it</Link> — curators research every
        suggestion, and accepted ones become new entries that every EduFadder user receives automatically.
      </div>
    </div>
  );
}
